import { Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { VmMatch } from '@common/dto/my';
import {
  ChatMessage,
  MessagesSummaryItem,
  UserNotification,
} from '@common/dto/my/chat';
import { select } from '@ngneat/elf';
import {
  getActiveId,
  getAllEntities,
  getEntity,
  selectActiveId,
  selectAllEntities,
  selectEntitiesCountByPredicate,
  selectEntity,
  selectManyByPredicate,
} from '@ngneat/elf-entities';
import { RemoteData, loadingState, successState } from '@ngspot/remote-data';
import {
  Observable,
  auditTime,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
} from 'rxjs';
import { filterOutNullish } from '../../../utils/rxjs/filterOutNullish';
import { RouterAuxService } from '../state/router-aux.service';
import {
  StateProps,
  StateRepository,
  chatEntitiesRef,
  matchEntitiesRef,
  messageSummaryEntitiesRef,
  notificationEntitiesRef,
} from './state.repository';

class StoreQuery {
  constructor(protected readonly store: StateRepository['store']) {}
}

@Injectable()
export class StateQuery extends StoreQuery {
  user$: Observable<StateProps['user']>;

  searchCriteria$: Observable<StateProps['searchCriteria']>;

  messagesSummaryLoading$: Observable<StateProps['messagesSummaryLoading']>;

  matches$: Observable<VmMatch[]>;

  lastActiveMatchId$: Observable<VmMatch['tblUserId'] | undefined>;

  lastActiveMatch$: Observable<VmMatch | undefined>;

  routeMatchId$: Observable<VmMatch['tblUserId'] | null>;

  routeMatch$: Observable<VmMatch | undefined>;

  /**
   * null for when there is no next match
   * undefined for when match is loading
   */
  nextMatchId$: Observable<VmMatch['tblUserId'] | undefined | null>;

  nextMatch$: Observable<VmMatch | undefined | null>;

  unloadedNextMatch$: Observable<string>;

  hasNoMatches$: Observable<boolean>;

  vm$: Observable<[RemoteMatch, RemoteMatch]>;

  chatMessages$: Observable<ChatMessage[]>;

  messageSummaries$: Observable<MessagesSummaryItem[]>;

  newMessagesCount$: Observable<number>;

  notifications$: Observable<
    (UserNotification & {
      newMessagesCount: number;
    })[]
  >;

  notificationsCount$: Observable<number>;

  aboutMe$: Observable<string | undefined>;

  get user() {
    return this.store.query((x) => x.user);
  }

  get lastActiveMatchId() {
    return this.store.query(getActiveId);
  }

  constructor(
    state: StateRepository,
    private routerAuxService: RouterAuxService,
    private dateAdapter: DateAdapter<Date>
  ) {
    super(state['store']);

    this.searchCriteria$ = this.store.pipe(select((x) => x.searchCriteria));
    this.user$ = this.store.pipe(select((x) => x.user));
    this.hasNoMatches$ = this.store.pipe(select((x) => x.hasNoMatches));
    this.messagesSummaryLoading$ = this.store.pipe(
      select((x) => x.messagesSummaryLoading)
    );

    this.matches$ = this.store.pipe(
      selectAllEntities({ ref: matchEntitiesRef }),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.lastActiveMatchId$ = this.store.pipe(
      selectActiveId(),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.lastActiveMatch$ = combineLatest([
      this.lastActiveMatchId$,
      this.matches$,
    ]).pipe(
      map(([id, matches]) => matches.find((match) => match.tblUserId === id)),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.routeMatchId$ = this.routerAuxService.activatedRoute$.pipe(
      map((route) => route.snapshot.paramMap.get('matchId')),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.routeMatch$ = combineLatest([this.routeMatchId$, this.matches$]).pipe(
      map(([routeMatchId, matches]) =>
        matches.find((match) => match.tblUserId === routeMatchId)
      ),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.nextMatchId$ = this.routeMatch$.pipe(
      select((match) => match?.nextMatchId),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.nextMatch$ = combineLatest([this.nextMatchId$, this.matches$]).pipe(
      map(([id, matches]) => {
        if (id == null) {
          return undefined;
        }
        return matches.find((match) => match.tblUserId === id);
      }),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.unloadedNextMatch$ = this.nextMatchId$.pipe(
      filterOutNullish(),
      filter(
        (id) => !this.store.query(getEntity(id, { ref: matchEntitiesRef }))
      )
    );

    this.vm$ = combineLatest([
      this.routeMatch$.pipe(map(matchToRemoteMatch)),
      this.nextMatch$.pipe(map(matchToRemoteNextMatch)),
    ]);

    this.chatMessages$ = this.routeMatchId$.pipe(
      switchMap((id) =>
        this.store.pipe(
          selectManyByPredicate((x) => x.matchId === id, {
            ref: chatEntitiesRef,
          }),
          map((messages) =>
            messages.sort(
              (a, b) => a.insertDate.getTime() - b.insertDate.getTime()
            )
          )
        )
      )
    );

    this.messageSummaries$ = this.store.pipe(
      selectAllEntities({ ref: messageSummaryEntitiesRef }),
      map((entities) =>
        entities.sort((a, b) =>
          this.dateAdapter.compareDate(
            b.lastMessageInsertDate,
            a.lastMessageInsertDate
          )
        )
      ),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.newMessagesCount$ = this.messageSummaries$.pipe(
      map((entries) => {
        return entries.reduce((acc, next) => {
          return acc + next.newMessagesCount;
        }, 0);
      })
    );

    this.notifications$ = combineLatest([
      this.store.pipe(
        selectAllEntities({ ref: notificationEntitiesRef }),
        map((entries) =>
          entries.sort((a, b) =>
            this.dateAdapter.compareDate(b.insertDate, a.insertDate)
          )
        )
      ),
      this.messageSummaries$,
    ]).pipe(
      auditTime(0),
      map(([notifications, messages]) => {
        const result: (UserNotification & {
          newMessagesCount: number;
        })[] = new Array(notifications.length).fill(undefined);

        type NotificationIndex = number;
        const trackingMap = new Map<string, NotificationIndex>();

        for (let i = 0; i < notifications.length; i++) {
          const notification = notifications[i];

          const messageSummary = messages.find(
            (x) => x.matchId === notification.matchId
          );
          const newMessagesCount = messageSummary?.newMessagesCount ?? 0;

          if (trackingMap.has(notification.matchId)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const existingNotificationIndex = trackingMap.get(
              notification.matchId
            )!;
            if (notification.tblNotificationId === 2) {
              // we know that this is a message notification, so we can convert
              // previous notifications to smile type
              result[existingNotificationIndex].tblNotificationId = 1;
            }
            result[existingNotificationIndex].newMessagesCount = 1;
          } else {
            trackingMap.set(notification.matchId, i);
          }

          result[i] = { ...notification, newMessagesCount };
        }

        return result;
      }),
      shareReplay(1)
    );

    this.notificationsCount$ = this.store.pipe(
      selectEntitiesCountByPredicate((x) => x.isNew, {
        ref: notificationEntitiesRef,
      })
    );

    this.aboutMe$ = this.store.pipe(select((x) => x.user?.aboutMe));
  }

  hasMessagesWithMatch(matchId: VmMatch['tblUserId']): boolean {
    return (
      this.store
        .query(getAllEntities({ ref: messageSummaryEntitiesRef }))
        .filter((x) => x.matchId === matchId).length > 0
    );
  }

  selectMessageSummary(matchId: VmMatch['tblUserId']) {
    return this.store.pipe(
      selectEntity(matchId, { ref: messageSummaryEntitiesRef })
    );
  }
}

let loadingMatchCounter = 0;

function matchToRemoteMatch(match: VmMatch | undefined): RemoteMatch {
  if (match) {
    loadingMatchCounter++;
  }
  const id = loadingMatchCounter.toString();
  const data: RemoteData<VmMatch> = match
    ? successState(match)
    : loadingState();
  return { id, data };
}

/**
 *
 * @param match
 * null for when there is no next match
 * undefined for when match is loading
 */
function matchToRemoteNextMatch(
  match: VmMatch | null | undefined
): RemoteMatch {
  const id = (loadingMatchCounter + 1).toString();
  const data: RemoteData<VmMatch | null> =
    match != null
      ? successState(match)
      : match === undefined
      ? loadingState()
      : successState(null);
  return { id, data };
}

export interface RemoteMatch<T = RemoteData<VmMatch | null>> {
  id: VmMatch['tblUserId'];
  data: T;
}
